// src/components/marketing/SiteFooter.js
// Shared footer, mirroring the legacy site: a "Stay Updated" band, the link
// columns, and the legal fine print.

import { Link } from 'react-router-dom';
import { LEGACY, COLORS, FONTS, HEADING } from 'styles/theme';
import NewsletterSignup from './NewsletterSignup';

const linkStyle = {
  display: 'block', color: '#bbb', textDecoration: 'none', fontSize: '13px', marginBottom: '8px',
};

const legalStyle = { color: '#bbb', textDecoration: 'none' };

const col = (title, links) => (
  <div>
    <div style={{ color: '#fff', fontWeight: 700, fontSize: '13px', letterSpacing: '1px', marginBottom: '12px' }}>
      {title}
    </div>
    {links.map(l => (
      l.to
        ? <Link key={l.label} to={l.to} style={linkStyle}>{l.label}</Link>
        : (
          <a
            key={l.label}
            href={l.href}
            style={linkStyle}
            {...(l.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {l.label}
          </a>
        )
    ))}
  </div>
);

// Brand glyphs rather than the platform names — the marks are recognised
// faster than the words and cost nothing but a path each.
const SOCIAL = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/getsquarefare/?hl=en',
    path: 'M12 2.2c3.2 0 3.6 0 4.9.07 1.2.05 1.8.25 2.2.42.6.22 1 .48 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c0 1.2-.2 1.8-.4 2.2a3.8 3.8 0 0 1-.9 1.4c-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2 0-1.8-.2-2.2-.4a3.8 3.8 0 0 1-1.4-.9 3.8 3.8 0 0 1-.9-1.4c-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.07-4.9c.05-1.2.25-1.8.42-2.2.22-.6.48-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zm0 1.8c-3.1 0-3.5 0-4.7.07-1.1.05-1.7.24-2.1.4-.5.2-.9.44-1.3.84-.4.4-.64.8-.84 1.3-.16.4-.35 1-.4 2.1C2.6 8.5 2.6 8.9 2.6 12s0 3.5.07 4.7c.05 1.1.24 1.7.4 2.1.2.5.44.9.84 1.3.4.4.8.64 1.3.84.4.16 1 .35 2.1.4 1.2.07 1.6.07 4.7.07s3.5 0 4.7-.07c1.1-.05 1.7-.24 2.1-.4.5-.2.9-.44 1.3-.84.4-.4.64-.8.84-1.3.16-.4.35-1 .4-2.1.07-1.2.07-1.6.07-4.7s0-3.5-.07-4.7c-.05-1.1-.24-1.7-.4-2.1a3.5 3.5 0 0 0-.84-1.3 3.5 3.5 0 0 0-1.3-.84c-.4-.16-1-.35-2.1-.4-1.2-.07-1.6-.07-4.7-.07zm0 3.1a5.1 5.1 0 1 1 0 10.2 5.1 5.1 0 0 1 0-10.2zm0 1.8a3.3 3.3 0 1 0 0 6.6 3.3 3.3 0 0 0 0-6.6zm5.3-3.2a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4z',
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/eatsquarefare/',
    path: 'M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12z',
  },
];

export default function SiteFooter() {
  return (
    <>
      {/* The sand ground is the fallback: if the photo is missing this reads as
          the plain band it was before, rather than breaking. */}
      <NewsletterSignup
        source="footer"
        image={`${process.env.PUBLIC_URL || ''}/images/shared/newsletter-band.webp`}
        style={{ background: COLORS.creamAlt }}
      />
      <footer style={{ background: COLORS.dark, color: '#bbb', padding: '48px 28px 28px' }}>
        <div style={{
          maxWidth: '1000px', margin: '0 auto', display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '32px', marginBottom: '32px',
        }}>
          <div>
            <div style={{ fontFamily: FONTS.serif, color: '#fff', fontSize: '22px', letterSpacing: '1px', marginBottom: '12px' }}>
              SQUARE FARE
            </div>
            <p style={{ fontSize: '13px', lineHeight: 1.6, margin: '0 0 18px' }}>
              Personalized, ready-to-eat meals tailored to your nutritional needs.
            </p>
            <div style={{ display: 'flex', gap: '14px' }}>
              {SOCIAL.map(s => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#bbb', display: 'inline-flex' }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d={s.path} />
                  </svg>
                </a>
              ))}
            </div>
          </div>
          {col('LEARN', [
            { label: 'About Us', to: '/about' },
            { label: 'The Science', to: '/science' },
            { label: 'What We Help With', to: '/health-goals' },
            { label: 'Health Conditions', to: '/health-conditions' },
            { label: 'For Nutritionists', to: '/nutritionists' },
            { label: 'Become a Partner', to: '/contact-form-partners' },
            { label: 'Blog', href: `${LEGACY}/blog` },
          ])}
          {/* Legacy Login sits directly under Contact Us: existing subscribers still
              manage their plan on the Squarespace site until they are migrated,
              and they look for it in Help rather than in the fine print. */}
          {col('HELP', [
            { label: 'FAQ', to: '/faqs' },
            { label: 'How To Order', to: '/how-to-order' },
            { label: 'Contact Us', to: '/contact-form-general' },
            { label: 'Legacy Login', href: LEGACY, external: true },
          ])}
          <div style={{ textAlign: 'right' }}>
            <div style={{ ...HEADING, color: '#fff', fontSize: '26px', marginBottom: '8px' }}>
              Get ready to feel amazing
            </div>
            <p style={{ fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
              Unlock your personalized nutritional journey.
            </p>
          </div>
        </div>
        <div style={{
          maxWidth: '1000px', margin: '0 auto', borderTop: '1px solid #333', paddingTop: '20px',
          fontSize: '12px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px',
        }}>
          <span>© {new Date().getFullYear()} Square Fare Inc. All rights reserved.</span>
          <span style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
            <Link to="/terms-and-conditions" style={legalStyle}>Terms</Link>
            <Link to="/privacy-policy" style={legalStyle}>Privacy</Link>
            <Link to="/hipaa-notice" style={legalStyle}>HIPAA Notice</Link>
          </span>
        </div>
      </footer>
    </>
  );
}
